import axios from "axios";
import { centralizedHost } from "~/Utils";
import { decodeHTML } from "~/Utils/CommonUtils";
import { ModuleFields } from "~/Utils/drilldownData";

export interface OptionType {
  label: string;
  value: string;
}
export type AsyncSelectField = ModuleFields & {
  loadOptions?: () => Promise<OptionType[]>;
};

export const fetchDataAndTransform = async (
  url: string,
  labelKey: string,
  valueKey?: string,
  filterFn?: (item: any) => boolean
): Promise<OptionType[]> => {
  try {
    const res = await axios.get(`${centralizedHost}${url}`);

    if (res?.data) {
      const filteredData = res.data.filter((item: any) => {
        if (!item.published) return false;
        return filterFn ? filterFn(item) : true;
      });

      return filteredData.map((item: any) => ({
        label: decodeHTML(item[labelKey]),
        value: valueKey ? decodeHTML(String(item[valueKey])) : decodeHTML(item[labelKey]),
      }));
    }
    return [];
  } catch (err) {
    console.error("Error fetching data:", err);
    return [];
  }
};

export const fetchDataAndTransformNumber = async (
  url: string,
  labelKey: string,
  valueKey?: string,
  filterFn?: (item: any) => boolean
): Promise<OptionType[]> => {
  try {
    const res = await axios.get(`${centralizedHost}${url}`);
    if (res?.data) {
      const filteredData = res.data.filter((item: any) => {
        if (!item.published) return false;
        return filterFn ? filterFn(item) : true;
      });

      return filteredData.map((item: any) => ({
        label: decodeHTML(item[labelKey]),
        value: valueKey ? decodeHTML(item[valueKey]) : decodeHTML(item[labelKey]),
      }));
    }
    return [];
  } catch (err) {
    console.error("Error fetching data:", err);
    return [];
  }
};

export const fetchDataAndTransformStore = async (
  url: string,
  labelKey: string,
  valueKey?: string,
  filterFn?: (item: any) => boolean
): Promise<OptionType[]> => {
  try {
    const res = await axios.get(`${centralizedHost}${url}`);

    const list = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];

    console.log(`[API RESPONSE NORMALIZED] ${url}`, list);

    const filteredData = list.filter((item: any) => {
      if (item.published === false) return false;
      return filterFn ? filterFn(item) : true;
    });

    return filteredData.map((item: any) => ({
      label: decodeHTML(item[labelKey]),
      value: valueKey ? decodeHTML(item[valueKey]) : decodeHTML(item[labelKey]),
    }));
  } catch (err) {
    console.error(`[API ERROR] ${url}`, err);
    return [];
  }
};

export const fetchDataAndTransformOffer = async (
  url: string,
  labelKey: string,
  valueKey?: string,
  filterFn?: (item: any) => boolean
): Promise<OptionType[]> => {
  try {
    const res = await axios.get(`${centralizedHost}${url}`);

    if (res?.data) {
      const filteredData = res.data.filter((item: any) => {
        if (!item.published) return false;
        return filterFn ? filterFn(item) : true;
      });

      return filteredData.map((item: any) => {
        const rawValue = valueKey ? item[valueKey] : item[labelKey];

        return {
          label: decodeHTML(String(item[labelKey])), // labels should always be string
          value:
            typeof rawValue === "number"
              ? rawValue // ✅ keep number as number
              : decodeHTML(String(rawValue)), // decode only strings
        };
      });
    }

    return [];
  } catch (err) {
    console.error("Error fetching data:", err);
    return [];
  }
};

export const fetchTagsShopSelection = () => fetchDataAndTransform("/api/tags-shop-dine", "name", "id");
export const fetchTypeShopSelection = () => fetchDataAndTransformNumber("/api/type-shop-dine", "name", "id");
export const fetchTypeShopStoreSelection = () => fetchDataAndTransform("/api/type-shop-dine", "name", "id");
export const fetchBrandSelection = () => fetchDataAndTransform("/api/brand", "name", "id");
export const fetchClassificationSelection = () => fetchDataAndTransform("/api/data-classification", "name", "id");
export const fetchCategorySelection = () => fetchDataAndTransformNumber("/api/shop-category-type-child", "name", "id");
export const fetchSubCategorySelection = () => fetchDataAndTransform("/api/sub_category", "name", "id");
export const fetchSourceSelection = () => fetchDataAndTransformNumber("/api/source", "name", "id");
export const fetchRedirectSelection = () => fetchDataAndTransformNumber("/api/redirect", "name", "id");

// export const fetchPrivacyPolicySelection = () =>
//   fetchDataAndTransformNumber("/api/privacy-policy/tab-1", "tab_title_english", "id");

export const fetchPrivacyPolicySelection = () =>
  fetchDataAndTransformNumber(
    "/api/privacy-policy/tab-1",
    "tab_title_english",
    "id",
    (item) => item.tab_title_english && item.tab_title_english.trim() !== ""
  );

export const fetchNotificationTypeSelection = () => fetchDataAndTransformNumber("/api/notification-type", "name", "id");
export const fetchRepeatTypeSelection = () => fetchDataAndTransformNumber("/api/repeat-type", "name", "id");
export const fetchPromotionMyAirportSelection = () =>
  fetchDataAndTransformNumber("/api/promotions-my-airports", "title", "id");
export const fetchLocationMyAirportSelection = () => fetchDataAndTransformNumber("/api/location", "level", "id");
export const fetchLGeoLocationPromotionSelection = () =>
  fetchDataAndTransformNumber("/api/geo-location-promotions", "header", "id");
export const fetchGeoLocationPromotionLevelsSelection = () =>
  fetchDataAndTransformOffer("/api/geo-location-promotion-levels", "technical_id", "id");
// export const fetchLocationSelection = () => fetchDataAndTransformStore("/api/store-location-dropdown", "name", "id");
